import { useCallback, useEffect, useState } from 'react'
import type {
    ChecklistItem,
    ItemScore,
    MaintenanceEvent,
    SurveyAnswer,
} from '~/api/types'
import {
    loadMaintenanceAssessmentContext,
    loadMaintenanceHistory,
} from './maintenance-resource-loaders'
import { errMessage } from './utils'

/** 선택된 평가의 유지하기 이력과 영향 상세 표시 리소스를 관리한다. */
export function useMaintenanceResources({
    selectedId,
    enabled,
}: {
    selectedId: string
    enabled: boolean
}) {
    const [history, setHistory] = useState<MaintenanceEvent[] | null>(null)
    const [checklist, setChecklist] = useState<ChecklistItem[]>([])
    const [answers, setAnswers] = useState<SurveyAnswer[]>([])
    const [scores, setScores] = useState<ItemScore[]>([])
    const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
    const [selectedImpactId, setSelectedImpactId] = useState<string | null>(
        null
    )
    const [loadError, setLoadError] = useState<string | null>(null)

    const resetResourceState = useCallback(() => {
        setHistory(null)
        setChecklist([])
        setAnswers([])
        setScores([])
        setSelectedEventId(null)
        setSelectedImpactId(null)
        setLoadError(null)
    }, [])

    const resetActiveSelection = useCallback(() => {
        setSelectedEventId(null)
        setSelectedImpactId(null)
    }, [])

    const addEventToHistory = useCallback((event: MaintenanceEvent) => {
        setHistory((prev) => [event, ...(prev ?? [])])
        setSelectedEventId(event.id)
        setSelectedImpactId(null)
    }, [])

    const updateEventInHistory = useCallback((event: MaintenanceEvent) => {
        setHistory((prev) =>
            (prev ?? []).map((item) => (item.id === event.id ? event : item))
        )
        setSelectedEventId(event.id)
    }, [])

    useEffect(() => {
        if (!selectedId || !enabled) {
            resetResourceState()
            return
        }

        let cancelled = false
        resetResourceState()

        loadMaintenanceHistory(selectedId)
            .then((items) => {
                if (!cancelled) setHistory(items)
            })
            .catch((err: unknown) => {
                if (cancelled) return
                setHistory([])
                setLoadError(errMessage(err, '이력을 불러오지 못했어요.'))
            })

        void loadMaintenanceAssessmentContext(selectedId).then((context) => {
            if (cancelled) return
            setChecklist(context.checklist)
            setAnswers(context.answers)
            setScores(context.scores)
        })

        return () => {
            cancelled = true
        }
    }, [enabled, resetResourceState, selectedId])

    return {
        history,
        checklist,
        answers,
        scores,
        selectedEventId,
        setSelectedEventId,
        selectedImpactId,
        setSelectedImpactId,
        loadError,
        resetActiveSelection,
        resetResourceState,
        addEventToHistory,
        updateEventInHistory,
    }
}
